import { useCallback, useEffect, useState } from 'react'

import { useBranch } from '@/contexts/BranchContext'
import { fetchOnlineBookingInboxSummary } from '@/lib/api/publicBooking'
import {
  countUnreadOnlineBookings,
  getUnreadOnlineBookings,
  markOnlineBookingSeen,
  markOnlineBookingsSeen,
  ONLINE_BOOKING_INBOX_EVENT,
} from '@/lib/onlineBookingInbox'

const POLL_MS = 45_000

/** @typedef {{ id: number, created_at?: string | null, customer_name?: string | null, starts_at?: string | null }} InboxPendingItem */

/**
 * Polls pending online bookings and tracks unread count per booking.
 */
export function useOnlineBookingInbox() {
  const { activeBranchId } = useBranch()
  const [pendingCount, setPendingCount] = useState(0)
  const [unreadCount, setUnreadCount] = useState(0)
  /** @type {[InboxPendingItem[], import('react').Dispatch<import('react').SetStateAction<InboxPendingItem[]>>]} */
  const [pendingItems, setPendingItems] = useState([])
  /** @type {[InboxPendingItem[], import('react').Dispatch<import('react').SetStateAction<InboxPendingItem[]>>]} */
  const [unreadItems, setUnreadItems] = useState([])
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    if (!activeBranchId) {
      setPendingCount(0)
      setUnreadCount(0)
      setPendingItems([])
      setUnreadItems([])
      setLoading(false)
      return null
    }

    try {
      const data = await fetchOnlineBookingInboxSummary()
      const pending = data.pending ?? []
      setPendingCount(data.pending_count ?? pending.length)
      setPendingItems(pending)
      setUnreadItems(getUnreadOnlineBookings(pending, activeBranchId))
      setUnreadCount(countUnreadOnlineBookings(pending, activeBranchId))
      return data
    } catch {
      setPendingCount(0)
      setUnreadCount(0)
      setPendingItems([])
      setUnreadItems([])
      return null
    } finally {
      setLoading(false)
    }
  }, [activeBranchId])

  useEffect(() => {
    refresh()
  }, [refresh])

  useEffect(() => {
    if (!activeBranchId) {
      return undefined
    }

    const timer = window.setInterval(refresh, POLL_MS)
    const onChanged = () => refresh()

    window.addEventListener(ONLINE_BOOKING_INBOX_EVENT, onChanged)

    return () => {
      window.clearInterval(timer)
      window.removeEventListener(ONLINE_BOOKING_INBOX_EVENT, onChanged)
    }
  }, [activeBranchId, refresh])

  const markBookingSeen = useCallback(
    (appointmentId) => {
      if (!activeBranchId || !appointmentId) {
        return
      }

      markOnlineBookingSeen(activeBranchId, appointmentId)
    },
    [activeBranchId],
  )

  const markAllSeen = useCallback(() => {
    if (!activeBranchId) {
      return
    }

    markOnlineBookingsSeen(
      activeBranchId,
      pendingItems.map((item) => item.id),
    )
    setUnreadCount(0)
    setUnreadItems([])
  }, [activeBranchId, pendingItems])

  return {
    pendingCount,
    unreadCount,
    pendingItems,
    unreadItems,
    loading,
    refresh,
    markBookingSeen,
    markAllSeen,
  }
}
